import { request } from "../api";
import { type DeviceListRow, unwrapList } from "./devices";

export type Role = "superadmin" | "platform_admin" | "member";

export interface UserRow {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  employee_code: string;
  is_active: boolean;
  last_login: string | null;
  devices_count: number;
  created_at: string;
  updated_at: string;
}

export type UserDetail = UserRow;

export interface UserSelectOption {
  id: string;
  label: string;
  email: string;
  full_name: string;
  role: Role;
}

export interface UserListFilters {
  role?: Role;
  is_active?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface UserCreateBody {
  email: string;
  full_name?: string;
  role: Role;
  employee_code?: string;
  is_active?: boolean;
  password?: string;
}

export interface UserUpdateBody {
  full_name?: string;
  role?: Role;
  employee_code?: string;
  is_active?: boolean;
  password?: string;
}

function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v === undefined || v === "" || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

interface Paginated<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

export const usersApi = {
  list: async (filters: UserListFilters = {}) => {
    const data = await request<Paginated<UserRow> | UserRow[]>(
      `/api/admin/v1/users/${qs(filters)}`,
    );
    return unwrapList(data);
  },
  get: (id: string) => request<UserDetail>(`/api/admin/v1/users/${id}/`),
  create: (body: UserCreateBody) =>
    request<UserDetail>(`/api/admin/v1/users/`, { method: "POST", json: body }),
  patch: (id: string, body: UserUpdateBody) =>
    request<UserDetail>(`/api/admin/v1/users/${id}/`, { method: "PATCH", json: body }),
  activate: (id: string) =>
    request<UserDetail>(`/api/admin/v1/users/${id}/activate/`, { method: "POST" }),
  deactivate: (id: string) =>
    request<UserDetail>(`/api/admin/v1/users/${id}/deactivate/`, { method: "POST" }),
  listDevices: (id: string) =>
    request<DeviceListRow[]>(`/api/admin/v1/users/${id}/devices/`),
  selectOptions: (filters: { search?: string; role?: Role } = {}) =>
    request<UserSelectOption[]>(`/api/admin/v1/users/select-options/${qs(filters)}`),
};
